/**
 * Экран «Обзор» — §14.2, первый из пяти.
 *
 * Отвечает на вопрос, ради которого дашборд открывают: «как идут дела».
 * Четыре плитки, график выручки и метка актуальности — и ничего больше:
 * экран, отвечающий на десять вопросов сразу, не отвечает ни на один.
 *
 * **Один запрос на весь экран.** §14.4 требует загрузки не дольше двух
 * секунд на данных за тринадцать месяцев; четыре отдельных запроса за
 * плитками собирали бы контекст расчёта четыре раза ещё до первой цифры.
 * Сервер отдаёт всё разом — `/api/finance/overview`.
 *
 * **Клик по дню графика ведёт в чеки этого дня** (§14.3). Цепочка
 * раскрытия начинается здесь: выручка за период → день → чеки дня.
 */

import { useQuery } from '@tanstack/react-query';
import { Empty, Loading } from '@shell/states';
import { financeApi } from '../api';
import type { Overview as OverviewData, PeriodParams } from '../api';
import { RevenueSeries } from '../charts/Charts';
import type { SeriesPoint } from '../charts/Charts';
import {
  DownloadLink,
  ExplanationModal,
  FreshnessBadge,
  MetricTile,
  PeriodPicker,
  ScreenError,
  Section,
  useExplanation,
} from '../components';

export function Overview({
  period,
  onPeriodChange,
  onOpenDay,
}: {
  period: PeriodParams;
  onPeriodChange: (next: PeriodParams) => void;
  onOpenDay: (day: string) => void;
}) {
  const query = useQuery({
    queryKey: ['finance', 'overview', period],
    queryFn: () => financeApi.overview(period),
  });
  const [explained, explain, closeExplanation] = useExplanation();

  if (query.isLoading) return <Loading />;
  // Период остаётся на экране: чаще всего чинить надо именно его (§8
  // правил интерфейса — кнопки, ведущей в тупик, быть не должно).
  if (query.error) {
    return (
      <ScreenError
        error={query.error}
        period={period}
        anchor={period.to ?? null}
        onPeriodChange={onPeriodChange}
        onRetry={() => query.refetch()}
      />
    );
  }

  const data = query.data as OverviewData;

  return (
    <div className="screen">
      {/* Метка — первым делом и до цифр: §16.3. Человек должен узнать,
          на что он смотрит, раньше, чем увидит сумму. */}
      <FreshnessBadge freshness={data.freshness} />

      <PeriodPicker period={period} anchor={data.period.to} onChange={onPeriodChange} />

      {!data.started ? (
        // Пустая система: причина и следующий шаг, а не пустые оси
        // (§2 правил интерфейса).
        <Empty
          title="Цифр пока нет"
          text={
            'Ни одной закрытой смены в базе. Так и должно быть до первой ' +
            'выгрузки из кассы — что с каналом, видно в разделе «Данные».'
          }
        />
      ) : (
        <>
          <div className="tiles">
            {data.tiles.map((tile) => (
              <MetricTile key={tile.code} metric={tile} onExplain={explain} />
            ))}
          </div>

          <Section
            title="Динамика"
            actions={
              <DownloadLink href={financeApi.csvUrl('revenue.dynamics', period)}>
                Выгрузить CSV
              </DownloadLink>
            }
          >
            <RevenueSeries
              rows={(data.series?.rows ?? []) as SeriesPoint[]}
              onPointClick={onOpenDay}
            />
            <p className="faint chart-hint">
              Нажмите на день, чтобы увидеть чеки, из которых он состоит.
            </p>
          </Section>
        </>
      )}

      <ExplanationModal metric={explained} onClose={closeExplanation} />
    </div>
  );
}
